import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import { api } from "../api/client";
import type { OpeningHours, ReservationView, Seating } from "../api/types";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import SiteNav from "../components/SiteNav";
import { useBusiness } from "../data/BusinessContext";
import { useLanguage } from "../i18n/LanguageContext";
import { DAY_KEYS, dayKeyOf, isWithinHours, isoDate, parseIsoDate, toMinutes } from "../lib/hours";
import { siteImage, srcSet } from "../lib/images";
import "./ReservePage.css";

const OCCASIONS = ["occasionNone", "occasionBrunch", "occasionDate", "occasionBirthday", "occasionBusiness"] as const;

type Errors = Partial<Record<"date" | "time" | "name" | "phone" | "email", string>>;

const SEATINGS: Seating[] = ["any", "inside", "outside"];
const SEATING_LABEL = { any: "seatingAny", inside: "seatingInside", outside: "seatingOutside" } as const;

/** Minutes from now before the earliest bookable time today. */
const LEAD_MINUTES = 30;

/**
 * Table booking. Any time inside the opening hours of the chosen day is
 * fine (a Monday, being a Ruhetag, offers none), plus how many and whether
 * inside or out on the terrace.
 */
export default function ReservePage() {
  const { t, language, locale } = useLanguage();
  const business = useBusiness();

  const today = useMemo(() => new Date(), []);
  const [date, setDate] = useState(() => isoDate(nextOpenDay(business.hours, today)));
  const [time, setTime] = useState("");
  const [seating, setSeating] = useState<Seating>("any");
  const [guests, setGuests] = useState(2);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [occasion, setOccasion] = useState<(typeof OCCASIONS)[number]>("occasionNone");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<ReservationView | null>(null);

  const dateObj = parseIsoDate(date);
  const dayHours = dateObj ? business.hours[dayKeyOf(dateObj)] : undefined;
  const openDays = DAY_KEYS.filter((k) => business.hours[k]).map((k) => t(k)).join(", ");

  // The live hours arrive a moment after the first paint; if they turn the
  // preselected day into a Ruhetag, move to the next open one.
  useEffect(() => {
    setDate((current) => {
      const d = parseIsoDate(current);
      if (d && business.hours[dayKeyOf(d)]) return current;
      return isoDate(nextOpenDay(business.hours, today));
    });
  }, [business.hours, today]);

  const tooMany = guests > business.max_online_guests;

  const isPast = (d: Date, tm: string) => {
    const now = new Date();
    if (d.toDateString() !== now.toDateString()) return d < now && !isWithinHours(business.hours, d, tm);
    let minutes = toMinutes(tm);
    if (dayHours && minutes < toMinutes(dayHours.open)) minutes += 1440; // after midnight, still tonight
    return minutes < now.getHours() * 60 + now.getMinutes() + LEAD_MINUTES;
  };

  const validate = (): Errors => {
    const next: Errors = {};
    if (!date || !dateObj) next.date = t("requiredField");
    else if (!dayHours) next.date = t("noSlots");
    if (!time) next.time = t("requiredField");
    else if (dateObj && dayHours && !isWithinHours(business.hours, dateObj, time)) next.time = t("timeClosed");
    else if (dateObj && dayHours && isPast(dateObj, time)) next.time = t("pastSlot");
    if (!name.trim()) next.name = t("requiredField");
    if (!/^\+?[\d\s()/-]{6,}$/.test(phone.trim())) next.phone = t("invalidPhone");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = t("invalidEmail");
    return next;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || tooMany) return;

    setSending(true);
    try {
      const view = await api.reserve({
        date,
        time,
        guests,
        seating,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        occasion,
        notes: notes.trim(),
        language,
      });
      setDone(view);
    } finally {
      setSending(false);
    }
  };

  const whenLabel = (d: string, tm: string) => {
    const parsed = parseIsoDate(d);
    if (!parsed) return "";
    return `${parsed.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}, ${tm}`;
  };

  return (
    <div className="reserve-page">
      <SiteNav />

      <main className="site-inner reserve" id="main">
        <div className="reserve-copy">
          <Reveal>
            <p className="eyebrow">{t("reserveEyebrow")}</p>
            <h1 className="reserve-title">{t("reserveTitle")}</h1>
            <p className="lead">{t("reserveLead")}</p>
          </Reveal>

          <Reveal className="reserve-photo photo" delay={0.1} y={40}>
            <img src={siteImage("corner", 900)} srcSet={srcSet("/img/corner.jpg", [500, 900, 1400])} sizes="(max-width: 900px) 100vw, 45vw" alt="" loading="lazy" decoding="async" />
          </Reveal>

          <Reveal delay={0.15}>
            <a className="reserve-phone" href={`tel:${business.phone.replace(/\s/g, "")}`}>
              <span className="tiny">{t("callUs")}</span>
              <span className="display reserve-phone-number">{business.phone}</span>
            </a>
          </Reveal>
        </div>

        <Reveal className="reserve-card" delay={0.1}>
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                className="reserve-done"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <Check />
                <h2>{t("reserveSuccessTitle")}</h2>
                <p className="reserve-done-summary display">{t("reserveSummary", { guests: done.guests, when: whenLabel(done.date, done.time) })}</p>
                <p className="reserve-done-body">{t("reserveSuccessBody", { code: done.code, when: whenLabel(done.date, done.time) })}</p>
                <button className="btn btn-outline" onClick={() => { setDone(null); setTime(""); }}>
                  {t("reserveAnother")}
                </button>
              </motion.div>
            ) : (
              <motion.form key="form" className="reserve-form" onSubmit={submit} noValidate exit={{ opacity: 0 }}>
                <div className="reserve-row">
                  <div className="field">
                    <label className="label" htmlFor="r-date">{t("date")}</label>
                    <input
                      id="r-date"
                      className={`input${errors.date ? " is-invalid" : ""}`}
                      type="date"
                      min={isoDate(today)}
                      value={date}
                      onChange={(e) => { setDate(e.target.value); setTime(""); }}
                    />
                    {errors.date && <span className="error-text">{errors.date}</span>}
                  </div>

                  <div className="field">
                    <span className="label">{t("guests")}</span>
                    <div className="stepper reserve-stepper" role="group" aria-label={t("guests")}>
                      <button type="button" className="stepper-btn" onClick={() => setGuests((g) => Math.max(1, g - 1))} disabled={guests <= 1} aria-label="−">−</button>
                      <span className="stepper-value nums">{guests}</span>
                      <button type="button" className="stepper-btn" onClick={() => setGuests((g) => Math.min(25, g + 1))} disabled={guests >= 25} aria-label="+">+</button>
                    </div>
                  </div>
                </div>

                <div className="reserve-row">
                  <div className="field">
                    <label className="label" htmlFor="r-time">{t("time")}</label>
                    {dayHours ? (
                      <>
                        <input
                          id="r-time"
                          className={`input nums${errors.time ? " is-invalid" : ""}`}
                          type="time"
                          step={300}
                          value={time}
                          onChange={(e) => setTime(e.target.value)}
                        />
                        <span className="tiny reserve-open-hint nums">{t("openHint", { open: dayHours.open, close: dayHours.close })}</span>
                      </>
                    ) : (
                      <div className="reserve-closed" role="alert">
                        <strong>{t("closedDayTitle")}</strong>
                        <span>{t("closedDayBody")}</span>
                        {openDays && <span className="reserve-closed-days">{t("openDaysLabel", { days: openDays })}</span>}
                      </div>
                    )}
                    {errors.time && <span className="error-text">{errors.time}</span>}
                  </div>

                  <div className="field">
                    <span className="label">{t("seating")}</span>
                    <div className="dash-chips reserve-seating" role="radiogroup" aria-label={t("seating")}>
                      {SEATINGS.map((key) => (
                        <button
                          key={key}
                          type="button"
                          role="radio"
                          aria-checked={seating === key}
                          className={`chip${seating === key ? " is-active" : ""}`}
                          onClick={() => setSeating(key)}
                        >
                          {t(SEATING_LABEL[key])}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {tooMany && (
                    <motion.p
                      className="reserve-large"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      {t("largeGroupHint")}{" "}
                      <a className="link" href={`tel:${business.phone.replace(/\s/g, "")}`}>{business.phone}</a>
                    </motion.p>
                  )}
                </AnimatePresence>

                <div className="reserve-row">
                  <div className="field">
                    <label className="label" htmlFor="r-name">{t("name")}</label>
                    <input id="r-name" className={`input${errors.name ? " is-invalid" : ""}`} value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} />
                    {errors.name && <span className="error-text">{errors.name}</span>}
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="r-phone">{t("phone")}</label>
                    <input id="r-phone" className={`input${errors.phone ? " is-invalid" : ""}`} type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                    {errors.phone && <span className="error-text">{errors.phone}</span>}
                  </div>
                </div>

                <div className="reserve-row">
                  <div className="field">
                    <label className="label" htmlFor="r-email">{t("email")}</label>
                    <input id="r-email" className={`input${errors.email ? " is-invalid" : ""}`} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="r-occasion">{t("occasion")}</label>
                    <select id="r-occasion" className="input" value={occasion} onChange={(e) => setOccasion(e.target.value as (typeof OCCASIONS)[number])}>
                      {OCCASIONS.map((key) => (
                        <option key={key} value={key}>{t(key)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="field">
                  <label className="label" htmlFor="r-notes">{t("notes")}</label>
                  <textarea id="r-notes" className="input" value={notes} maxLength={300} placeholder={t("notesPlaceholder")} onChange={(e) => setNotes(e.target.value)} />
                </div>

                <button className={`btn btn-primary btn-lg${tooMany ? " is-incomplete" : ""}`} type="submit" disabled={sending} aria-disabled={tooMany}>
                  {sending ? t("sending") : t("submitReservation")}
                  {!sending && <span className="arrow">→</span>}
                </button>
                <p className="tiny reserve-privacy">
                  {t("reservePrivacy")} <Link className="link" to="/datenschutz">{t("privacy")}</Link>
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </Reveal>
      </main>

      <Footer business={business} />
    </div>
  );
}

/** The first day from today that has opening hours. */
function nextOpenDay(hours: OpeningHours, from: Date): Date {
  for (let ahead = 0; ahead < 8; ahead += 1) {
    const d = new Date(from);
    d.setDate(from.getDate() + ahead);
    if (hours[dayKeyOf(d)]) return d;
  }
  return from;
}

function Check() {
  return (
    <motion.svg
      width="56"
      height="56"
      viewBox="0 0 56 56"
      fill="none"
      className="reserve-check"
      initial="hidden"
      animate="visible"
      aria-hidden="true"
    >
      <motion.circle cx="28" cy="28" r="26" stroke="currentColor" strokeWidth="1.5" variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1 } }} transition={{ duration: 0.8, ease: "easeOut" }} />
      <motion.path d="M17 29l8 8 14-16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1 } }} transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }} />
    </motion.svg>
  );
}
